import React, { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';

import type { View } from 'common/types/view';
import type { GuidanceSummaryV2 } from 'common/types/approvals';

import { I18n } from 'common/i18n';
import { withGuidanceV2, mapPermissionScopeToTargetAudience } from 'common/core/approvals';
import currentUserCanManageViewers from 'common/views/current_user_can_manage_viewers';

import ActionItem from '../action_item';
import AccessManager, { AccessManagerProps } from 'common/components/AccessManager';
import { MODES as ACCESS_MANAGER_MODES } from 'common/components/AccessManager/Constants';
import { defaultAccessManagerOnConfirm, PermissionsSavedPayload } from 'common/components/AccessManager/Util';
import { requireApprovalRequestWithdrawalBeforeAction } from 'common/components/AssetActionBar/components/publication_action';
import { showToastNow, ToastType } from 'common/components/ToastNotification/Toastmaster';

export interface ManageViewersActionItemProps {
  approvalsGuidance: GuidanceSummaryV2;
  assetIsStoryDraft?: boolean;
  closeCurrentModal: () => void;
  revisionInfo?: { revisionSeq: number; link: string } | null;
  setCurrentModal: (
    modal: typeof AccessManager,
    props: Omit<AccessManagerProps, 'onDismiss' | 'view'>,
  ) => void;
  updateAudience?: (payload: PermissionsSavedPayload) => void;
  updateView?: () => void;
  view: View;
}

export const ManageViewersActionItem = ({
  approvalsGuidance,
  assetIsStoryDraft,
  closeCurrentModal,
  revisionInfo,
  setCurrentModal,
  updateAudience,
  updateView,
  view,
}: ManageViewersActionItemProps) => {
  const closeCurrentModalAndUpdate = useCallback(
    (updatePayload: PermissionsSavedPayload) => {
      const { permissions, mode } = updatePayload;
      const targetScope = permissions.scope;
      const targetAudience = mapPermissionScopeToTargetAudience(targetScope);
      const scopeChanging = targetScope !== view.permissions?.scope;
      const willEnterApprovalQueue =
        scopeChanging &&
        targetAudience &&
        withGuidanceV2(approvalsGuidance).willEnterApprovalQueue(targetAudience);

      // If the scope is changing and will be changed to public or internal audience,
      // we will need to check if the asset will require approval, and show a different message if it will.
      const toastKey = willEnterApprovalQueue
        ? 'shared.components.asset_action_bar.publication_action.submitted_asset_for_approval'
        : `shared.site_chrome.access_manager.${mode}.success_toast`;
      updatePayload.willEnterApprovalQueue = true;

      closeCurrentModal();
      updateAudience?.(updatePayload);
      // There are some elements of the view (notably the federatedTo list) which
      // changes when the audience changes. This ensures that those elements of the
      // view object stay up to date if someone repeatedly changes the audience.
      updateView?.();

      showToastNow({
        type: ToastType.SUCCESS,
        content: I18n.t(toastKey),
      });
    },
    [approvalsGuidance, closeCurrentModal, updateAudience, updateView, view],
  );

  const handleClick = useCallback(() => {
    const accessManagerProps = {
      mode: ACCESS_MANAGER_MODES.CHANGE_AUDIENCE,
      onConfirm: defaultAccessManagerOnConfirm(closeCurrentModalAndUpdate, false),
      approvalsGuidance,
    };

    return requireApprovalRequestWithdrawalBeforeAction(approvalsGuidance, () =>
      setCurrentModal(AccessManager, { ...accessManagerProps }),
    );
  }, [approvalsGuidance, closeCurrentModalAndUpdate, setCurrentModal]);

  // Don't render if:
  // - approvals guidance or view is empty
  // - view is locked
  // - asset is a revision or story draft
  // - user doesn't have permission to manage viewers
  // NOTE: assetIsStoryDraft is a necessary guard here, because window.IS_STORY_PUBLISHED will be missing
  //       which will cause currentUserCanManageViewers to Error if the asset is a story.
  if (
    isEmpty(approvalsGuidance) ||
    isEmpty(view) ||
    view.locked ||
    revisionInfo ||
    assetIsStoryDraft ||
    !currentUserCanManageViewers(view, approvalsGuidance)
  ) {
    return null;
  }

  const label = I18n.t('shared.asset_browser.result_list_table.action_dropdown.change_audience');

  return <ActionItem label={label} onClick={handleClick} />;
};
